import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./WholesaleAnalysis.module.scss";
import { Card, Row, Col, Table, Typography, Select, DatePicker, Spin, Alert, Tag, Space } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  PercentageOutlined,
  InfoCircleOutlined,
  BulbOutlined,
  AlertOutlined,
} from "@ant-design/icons";
import moment, { Moment } from "moment";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as CTitle,
  Legend,
  Tooltip as CTooltip,
} from "chart.js";
import { getWholesaleAnalysis, getWholesaleFilters } from "../../../api/Services";
import type {
  WholesaleAnalysisData,
  WholesaleAnalysisMessage,
  WholesaleCategoryMixRow,
  WholesaleClientRow,
} from "../../../types/wholesaleAnalysis";

ChartJS.register(CategoryScale, LinearScale, BarElement, CTitle, Legend, CTooltip);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const fmt = (v: number) =>
  `$ ${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => v.toLocaleString("es-AR");

const getDefaultSemesterRange = (): [Moment, Moment] => {
  const now = moment();
  if (now.month() < 6) {
    return [moment().startOf("year"), moment().month(5).endOf("month")];
  }
  return [moment().month(6).startOf("month"), moment().endOf("year")];
};

const Trend: React.FC<{ value: number | null; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  if (value === null || value === undefined) return null;
  const pos = value >= 0;
  return (
    <span className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}>
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      {pos ? "+" : ""}
      {value.toFixed(1)}
      {suffix}
    </span>
  );
};

const WholesaleAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<[Moment, Moment]>(getDefaultSemesterRange);
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [brandId, setBrandId] = useState<number | undefined>();
  const [wholesalerId, setWholesalerId] = useState<number | undefined>();
  const [categoryOptions, setCategoryOptions] = useState<{ value: number; label: string }[]>([]);
  const [brandOptions, setBrandOptions] = useState<{ value: number; label: string }[]>([]);
  const [data, setData] = useState<WholesaleAnalysisData | null>(null);
  const [loading, setLoading] = useState(false);

  const monthLabel = useCallback(
    (monthNumber: number) => t(`WholesaleAnalysis-Month-${monthNumber}`),
    [t]
  );

  const clientDisplayName = useCallback(
    (row: WholesaleClientRow) =>
      row.wholesalerId != null || row.clientId != null
        ? row.clientName
        : t("WholesaleAnalysis-DefaultClient"),
    [t]
  );

  useEffect(() => {
    getWholesaleFilters().then((res) => {
      setCategoryOptions(
        (res?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))
      );
      setBrandOptions((res?.brands ?? []).map((b) => ({ value: b.id, label: b.name })));
    });
  }, []);

  const categoryDisplayName = useCallback(
    (row: WholesaleCategoryMixRow) =>
      row.categoryId == null || !row.categoryName
        ? t("WholesaleAnalysis-NoCategory")
        : row.categoryName,
    [t]
  );

  const formatAnalysisMessage = useCallback(
    (msg: WholesaleAnalysisMessage) => {
      const params: Record<string, string | number> = { ...(msg.params ?? {}) };
      if ("name" in params && !params.name) {
        params.name = String(t("WholesaleAnalysis-NoName"));
      }
      return t(msg.key, params);
    },
    [t]
  );

  const severityLabel = useCallback(
    (severity: string) => t(`WholesaleAnalysis-Severity-${severity}`),
    [t]
  );

  const alertTypeLabel = useCallback(
    (type: string) => t(`WholesaleAnalysis-AlertType-${type}`),
    [t]
  );

  const loadData = useCallback(async () => {
    if (!dateRange[0] || !dateRange[1]) return;
    setLoading(true);
    const result = await getWholesaleAnalysis(
      dateRange[0].format("YYYY-MM-DD"),
      dateRange[1].format("YYYY-MM-DD"),
      categoryId,
      brandId,
      wholesalerId
    );
    setData(result);
    setLoading(false);
  }, [dateRange, categoryId, brandId, wholesalerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const kpis = data?.kpis;
  const b2b = data?.b2bComparison;
  const seasonality = data?.seasonality;

  const seasonChartData = useMemo(() => {
    if (!seasonality) return null;
    return {
      labels: seasonality.months.map((m) => monthLabel(m.monthNumber)),
      datasets: [
        {
          label: t("WholesaleAnalysis-Chart-Wholesale"),
          data: seasonality.months.map((m) => m.wholesaleSalesPct),
          backgroundColor: "rgba(109, 40, 217, 0.85)",
          borderRadius: 4,
        },
        {
          label: t("WholesaleAnalysis-Chart-Retail"),
          data: seasonality.months.map((m) => m.retailSalesPct),
          backgroundColor: "rgba(217, 217, 217, 0.9)",
          borderRadius: 4,
        },
      ],
    };
  }, [seasonality, monthLabel, t]);

  const seasonChartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "top" as const } },
    scales: {
      y: { beginAtZero: true, ticks: { callback: (v: number | string) => `${v}%` } },
      x: { grid: { display: false } },
    },
  };

  const clientColumns = useMemo(
    () => [
      {
        title: data?.configReady
          ? t("WholesaleAnalysis-Column-Wholesaler")
          : t("WholesaleAnalysis-Column-Client"),
        dataIndex: "clientName",
        key: "clientName",
        render: (_: string, row: WholesaleClientRow) => (
          <Text strong>{clientDisplayName(row)}</Text>
        ),
      },
      {
        title: t("WholesaleAnalysis-Column-Sales"),
        dataIndex: "sales",
        key: "sales",
        render: (v: number) => fmt(v),
        align: "right" as const,
      },
      {
        title: t("WholesaleAnalysis-Column-Margin"),
        dataIndex: "marginPct",
        key: "marginPct",
        render: (v: number) => `${v.toFixed(1)}%`,
        align: "right" as const,
      },
      {
        title: t("WholesaleAnalysis-Column-AvgTicket"),
        dataIndex: "avgTicket",
        key: "avgTicket",
        render: (v: number) => fmt(v),
        align: "right" as const,
      },
      {
        title: t("WholesaleAnalysis-Column-WholesaleSalesPct"),
        dataIndex: "salesPctOfOrg",
        key: "salesPctOfOrg",
        render: (v: number) => `${v.toFixed(1)}%`,
        align: "right" as const,
      },
    ],
    [t, clientDisplayName, data?.configReady]
  );

  const mixColumns = useMemo(
    () => [
      {
        title: t("WholesaleAnalysis-Column-Category"),
        dataIndex: "categoryName",
        key: "categoryName",
        render: (_: string, row: WholesaleCategoryMixRow) => categoryDisplayName(row),
      },
      {
        title: t("WholesaleAnalysis-Column-Sales"),
        dataIndex: "sales",
        key: "sales",
        render: (v: number) => fmt(v),
        align: "right" as const,
      },
      {
        title: t("WholesaleAnalysis-Column-Participation"),
        dataIndex: "salesPct",
        key: "salesPct",
        render: (v: number) => `${v.toFixed(1)}%`,
        align: "right" as const,
      },
      {
        title: t("WholesaleAnalysis-Column-VsLy"),
        dataIndex: "salesVariationLyPct",
        key: "salesVariationLyPct",
        render: (v: number | null) =>
          v !== null ? <Trend value={v} /> : <span>—</span>,
        align: "right" as const,
      },
    ],
    [t, categoryDisplayName]
  );

  const productColumns = useMemo(
    () => [
      {
        title: t("WholesaleAnalysis-Column-Product"),
        key: "producto",
        render: (_: unknown, r: WholesaleAnalysisData["topProducts"][0]) => (
          <div>
            <Text strong>{r.productName}</Text>
            {r.externalId && (
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {r.externalId}
                </Text>
              </div>
            )}
          </div>
        ),
      },
      {
        title: t("WholesaleAnalysis-Column-Units"),
        dataIndex: "units",
        key: "units",
        render: (v: number) => fmtN(v),
        align: "right" as const,
      },
      {
        title: t("WholesaleAnalysis-Column-Sales"),
        dataIndex: "sales",
        key: "sales",
        render: (v: number) => fmt(v),
        align: "right" as const,
      },
      {
        title: t("WholesaleAnalysis-Column-Margin"),
        dataIndex: "marginPct",
        key: "marginPct",
        render: (v: number) => `${v.toFixed(1)}%`,
        align: "right" as const,
      },
    ],
    [t]
  );

  const renderB2bMetricRow = (
    label: string,
    wholesaleHint: string,
    retailHint: string,
    diffHint: string,
    wholesaleValue: string,
    retailValue: string,
    diffPct: number | null
  ) => (
    <div className={styles.b2bMetricBlock} key={label}>
      <Text className={styles.b2bMetricTitle}>{label}</Text>
      <div className={styles.b2bMetricRow}>
        <div className={styles.b2bCol}>
          <span className={`${styles.b2bIcon} ${styles.b2bIconWholesale}`}>
            <UserOutlined />
          </span>
          <div>
            <Text className={styles.b2bColLabel}>{t("WholesaleAnalysis-B2b-Wholesale")}</Text>
            <div className={styles.b2bColValue}>{wholesaleValue}</div>
            <Text type="secondary" className={styles.b2bColHint}>
              {wholesaleHint}
            </Text>
          </div>
        </div>
        <div className={styles.b2bCol}>
          <span className={`${styles.b2bIcon} ${styles.b2bIconRetail}`}>
            <ShoppingCartOutlined />
          </span>
          <div>
            <Text className={styles.b2bColLabel}>{t("WholesaleAnalysis-B2b-Retail")}</Text>
            <div className={styles.b2bColValue}>{retailValue}</div>
            <Text type="secondary" className={styles.b2bColHint}>
              {retailHint}
            </Text>
          </div>
        </div>
        <div className={styles.b2bCol}>
          <span className={`${styles.b2bIcon} ${styles.b2bIconDiff}`}>
            <PercentageOutlined />
          </span>
          <div>
            <Text className={styles.b2bColLabel}>{t("WholesaleAnalysis-B2b-Difference")}</Text>
            <div className={styles.b2bColValue}>
              {diffPct !== null ? `${diffPct.toFixed(1)}%` : "—"}
            </div>
            <Text type="secondary" className={styles.b2bColHint}>
              {diffHint}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>
        {t("WholesaleAnalysis-PageTitle")}
      </Title>

      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("WholesaleAnalysis-Filter-Date")}</Text>
            <RangePicker
              className={styles.filterInput}
              value={dateRange}
              onChange={(dates) => {
                if (dates?.[0] && dates[1]) setDateRange([dates[0], dates[1]]);
              }}
              format="DD/MM/YYYY"
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("WholesaleAnalysis-Filter-Category")}</Text>
            <Select
              placeholder={t("WholesaleAnalysis-Filter-All-F")}
              allowClear
              className={styles.filterSelect}
              value={categoryId}
              onChange={(v) => setCategoryId(v)}
              options={categoryOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("WholesaleAnalysis-Filter-Brand")}</Text>
            <Select
              placeholder={t("WholesaleAnalysis-Filter-All-F")}
              allowClear
              className={styles.filterSelect}
              value={brandId}
              onChange={(v) => setBrandId(v)}
              options={brandOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("WholesaleAnalysis-Filter-Wholesaler")}</Text>
            <Select
              placeholder={t("WholesaleAnalysis-Filter-All-M")}
              allowClear
              disabled={!data?.configReady && (data?.clientOptions?.length ?? 0) === 0}
              className={styles.filterSelect}
              value={wholesalerId}
              onChange={(v) => setWholesalerId(v)}
              options={(data?.clientOptions ?? []).map((c) => ({
                value: c.id,
                label:
                  c.name?.trim() ||
                  `${t("WholesaleAnalysis-ClientOption")} ${c.id}`,
              }))}
            />
          </div>
        </div>
      </Card>

      <Spin spinning={loading}>
        {data && !data.configReady && (
          <Alert
            type="warning"
            showIcon
            className={styles.configAlert}
            message={t("WholesaleAnalysis-Config-MissingTitle")}
            description={t("WholesaleAnalysis-Config-MissingDesc")}
          />
        )}
        {kpis && (
          <>
            <div className={styles.kpiRow}>
              <Card className={styles.kpiCard}>
                <Text className={styles.kpiLabel}>{t("WholesaleAnalysis-Kpi-WholesaleSalesPct")}</Text>
                <div className={styles.kpiValueRow}>
                  <span className={styles.kpiValue}>{kpis.wholesaleSalesPct.toFixed(1)}%</span>
                  <Trend value={kpis.wholesaleSalesPctLy} />
                </div>
                <Text className={styles.kpiDesc}>
                  {`${fmt(kpis.wholesaleSales)} ${t("WholesaleAnalysis-Kpi-WholesaleSalesDesc")} ${fmt(kpis.totalSales)} ${t("WholesaleAnalysis-Kpi-WholesaleSalesDescTotal")}`}
                </Text>
              </Card>
              <Card className={styles.kpiCard}>
                <Text className={styles.kpiLabel}>{t("WholesaleAnalysis-Kpi-AvgTicket")}</Text>
                <div className={styles.kpiValueRow}>
                  <span className={styles.kpiValue}>{fmt(kpis.avgTicket)}</span>
                  <Trend value={kpis.avgTicketLy} />
                </div>
                <Text className={styles.kpiDesc}>
                  {`${fmtN(kpis.tickets)} ${t("WholesaleAnalysis-Kpi-Tickets")}`}
                </Text>
              </Card>
              <Card className={styles.kpiCard}>
                <Text className={styles.kpiLabel}>{t("WholesaleAnalysis-Kpi-Margin")}</Text>
                <div className={styles.kpiValueRow}>
                  <span className={styles.kpiValue}>{kpis.marginPct.toFixed(1)}%</span>
                  <Trend value={kpis.marginPctLy} />
                </div>
                <Text className={styles.kpiDesc}>{t("WholesaleAnalysis-Kpi-MarginDesc")}</Text>
              </Card>
              <Card className={styles.kpiCard}>
                <Text className={styles.kpiLabel}>{t("WholesaleAnalysis-Kpi-Clients")}</Text>
                <div className={styles.kpiValueRow}>
                  <span className={styles.kpiValue}>
                    {kpis.clientsCountAvailable && kpis.clientsCount !== null
                      ? kpis.clientsCount
                      : t("WholesaleAnalysis-Kpi-Na")}
                  </span>
                  {kpis.clientsCountAvailable && (
                    <Trend value={kpis.clientsCountLy} suffix="" />
                  )}
                </div>
                <Text className={styles.kpiDesc}>{t("WholesaleAnalysis-Kpi-ClientsDesc")}</Text>
              </Card>
            </div>

            {b2b && (
              <Card className={styles.b2bCard} bordered={false}>
                <Title level={5} className={styles.b2bCardTitle}>
                  {t("WholesaleAnalysis-Section-B2bComparison")}
                </Title>
                {renderB2bMetricRow(
                  t("WholesaleAnalysis-Metric-AvgTicket").toUpperCase(),
                  t("WholesaleAnalysis-B2b-WholesaleTicketHint"),
                  t("WholesaleAnalysis-B2b-RetailTicketHint"),
                  t("WholesaleAnalysis-B2b-DiffFormula"),
                  fmt(b2b.wholesaleAvgTicket),
                  fmt(b2b.retailAvgTicket),
                  b2b.avgTicketDiffPct
                )}
                {renderB2bMetricRow(
                  t("WholesaleAnalysis-Metric-Margin").toUpperCase(),
                  t("WholesaleAnalysis-B2b-WholesaleMarginHint"),
                  t("WholesaleAnalysis-B2b-RetailMarginHint"),
                  t("WholesaleAnalysis-B2b-DiffFormula"),
                  `${b2b.wholesaleMarginPct.toFixed(1)}%`,
                  `${b2b.retailMarginPct.toFixed(1)}%`,
                  b2b.marginDiffPct
                )}
                <div className={styles.b2bFootnote}>
                  <InfoCircleOutlined />
                  <Text type="secondary">{t("WholesaleAnalysis-B2b-Footnote")}</Text>
                </div>
              </Card>
            )}

            <Card
              className={styles.sectionCard}
              title={t("WholesaleAnalysis-Section-SalesByWholesaler")}
            >
              <Table
                columns={clientColumns}
                dataSource={(data?.clients ?? []).map((c, i) => ({ ...c, key: i }))}
                pagination={false}
                size="middle"
                className={styles.dataTable}
              />
            </Card>

            <Row gutter={[16, 16]} className={styles.mainRow}>
              <Col xs={24} lg={12}>
                <Card
                  className={styles.sectionCard}
                  title={t("WholesaleAnalysis-Section-CategoryMix")}
                >
                  <Table
                    columns={mixColumns}
                    dataSource={(data?.categoryMix ?? []).map((r, i) => ({ ...r, key: i }))}
                    pagination={false}
                    size="middle"
                    className={styles.dataTable}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  className={styles.sectionCard}
                  title={t("WholesaleAnalysis-Section-TopProducts")}
                >
                  <Table
                    columns={productColumns}
                    dataSource={(data?.topProducts ?? []).map((r, i) => ({ ...r, key: i }))}
                    pagination={false}
                    size="middle"
                    className={styles.dataTable}
                  />
                </Card>
              </Col>
            </Row>

            {seasonality && seasonChartData && (
              <Card
                className={styles.sectionCard}
                title={t("WholesaleAnalysis-Section-Seasonality")}
              >
                <div className={styles.chartWrap}>
                  <Bar data={seasonChartData} options={seasonChartOpts} />
                </div>
                <div className={styles.seasonCards}>
                  <div className={styles.seasonCard}>
                    <span className={styles.seasonCardLabel}>
                      {t("WholesaleAnalysis-Season-WholesalePeak")}
                    </span>
                    <div className={styles.seasonCardValue}>
                      {monthLabel(seasonality.wholesalePeakMonth)}
                    </div>
                  </div>
                  <div className={styles.seasonCard}>
                    <span className={styles.seasonCardLabel}>
                      {t("WholesaleAnalysis-Season-RetailPeak")}
                    </span>
                    <div className={styles.seasonCardValue}>
                      {monthLabel(seasonality.retailPeakMonth)}
                    </div>
                  </div>
                  <div className={styles.seasonCard}>
                    <span className={styles.seasonCardLabel}>
                      {t("WholesaleAnalysis-Season-AvgLead")}
                    </span>
                    <div className={styles.seasonCardValue}>
                      {`${seasonality.peakLeadDays} ${t("WholesaleAnalysis-Season-Days")}`}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <Row gutter={[16, 16]} className={styles.mainRow}>
              <Col xs={24} lg={12}>
                <Card
                  className={styles.sectionCard}
                  title={
                    <Space>
                      <BulbOutlined style={{ color: "#faad14" }} />
                      <span>{t("WholesaleAnalysis-Section-Insights")}</span>
                    </Space>
                  }
                >
                  {(data?.insights ?? []).length > 0 ? (
                    <div className={styles.bulletList}>
                      {(data?.insights ?? []).map((ins, i) => (
                        <div key={i} className={styles.bulletItem}>
                          <BulbOutlined className={styles.bIconYellow} />
                          <Text>{formatAnalysisMessage(ins)}</Text>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary">{t("WholesaleAnalysis-Insights-Empty")}</Text>
                  )}
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  className={styles.sectionCard}
                  title={
                    <Space>
                      <AlertOutlined style={{ color: "#f5222d" }} />
                      <span>{t("WholesaleAnalysis-Section-Alerts")}</span>
                    </Space>
                  }
                >
                  {(data?.alerts ?? []).length > 0 ? (
                    <div className={styles.bulletList}>
                      {(data?.alerts ?? []).map((a, i) => (
                        <div key={i} className={styles.bulletItem}>
                          {a.severity && (
                            <Tag
                              color={
                                a.severity === "high"
                                  ? "red"
                                  : a.severity === "medium"
                                    ? "orange"
                                    : "default"
                              }
                              style={{ minWidth: 56, textAlign: "center" }}
                            >
                              {severityLabel(a.severity)}
                            </Tag>
                          )}
                          {a.type && (
                            <Tag color="purple" style={{ fontSize: 10 }}>
                              {alertTypeLabel(a.type)}
                            </Tag>
                          )}
                          <Text>{formatAnalysisMessage(a)}</Text>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary">{t("WholesaleAnalysis-Alerts-Empty")}</Text>
                  )}
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Spin>
    </div>
  );
};

export default WholesaleAnalysis;
